import { PlayerGear } from "./playerGear";
import { TrenchHud } from "../managers/trenchHud";
import { InventoryScreen } from "../managers/inventoryScreen";
import { emblemTexture } from "../utils/emblemRenderer";
import { type Emblem } from "@common/utils/emblem";
import { REACTION_CLIP_MS, REACTION_SHOWN_MS, storeEmblemTexture, storeReaction } from "../store/storeCatalog";
import { AnimationType, GameConstants, Layer, ObjectCategory, PlayerActions, SpectateActions, ZIndexes } from "@common/constants";
import { type EmoteDefinition, getBadgeIdString } from "@common/definitions/emotes";
import { Ammos } from "@common/definitions/items/ammos";
import { type ArmorDefinition, ArmorType } from "@common/definitions/items/armors";
import { type BackpackDefinition } from "@common/definitions/items/backpacks";
import { type GunDefinition, type SingleGunNarrowing } from "@common/definitions/items/guns";
import { type HealingItemDefinition, HealType } from "@common/definitions/items/healingItems";
import { DEFAULT_HAND_RIGGING, type MeleeDefinition } from "@common/definitions/items/melees";
import { type GravityBeamData } from "@common/definitions/gravity";
import { PerkData, PerkIds } from "@common/definitions/items/perks";
import { type SkinDefinition, Skins } from "@common/definitions/items/skins";
import { Loots, type WeaponDefinition } from "@common/definitions/loots";
import { MaterialSounds, type ObstacleDefinition } from "@common/definitions/obstacles";
import { SpectatePacket } from "@common/packets/spectatePacket";
import { getMeleeHitbox, getMeleeTargets } from "@common/utils/gameHelpers";
import { CircleHitbox } from "@common/utils/hitbox";
import { adjacentOrEqualLayer } from "@common/utils/layer";
import { Angle, EaseFunctions, Geometry, Numeric } from "@common/utils/math";
import { removeFrom, type Timeout } from "@common/utils/misc";
import { DefinitionType, type ReferenceTo } from "@common/utils/objectDefinitions";
import { type ObjectsNetData } from "@common/utils/objectsSerializations";
import { pickRandomInArray, random, randomBoolean, randomFloat, randomRotation, randomSign, randomVector } from "@common/utils/random";
import { FloorNames, FloorTypes } from "@common/utils/terrain";
import { Vec, type Vector } from "@common/utils/vector";
import $ from "jquery";
import { type ColorSource, Container, Graphics, GraphicsContext, ObservablePoint, Text } from "pixi.js";
import { GameConsole } from "../console/gameConsole";
import { Game } from "../game";
import { GravityFx } from "../gravity/gravityFx";
import { OctoFx } from "../octo/octoFx";
import { CameraManager } from "../managers/cameraManager";
import { CombatFx } from "../managers/combatFx";
import { CombatSounds } from "../managers/combatSounds";
import { type GoreHit, GoreManager } from "../managers/gore";
import { InputManager } from "../managers/inputManager";
import { JumpInView } from "../managers/jumpIn";
import { MapManager } from "../managers/mapManager";
import { Lighting } from "../managers/lighting";
import { NightLight } from "../managers/nightLight";
import { type ParticleEmitter, ParticleManager, type ParticleOptions } from "../managers/particleManager";
import { PerkManager } from "../managers/perkManager";
import { type GameSound, SoundManager } from "../managers/soundManager";
import { UIManager } from "../managers/uiManager";
import { BULLET_WHIZ_SCALE, DIFF_LAYER_HITBOX_OPACITY, HITBOX_COLORS, PIXI_SCALE, PLAYER_PARTICLE_ZINDEX, TEAMMATE_COLORS, UI_DEBUG_MODE } from "../utils/constants";
import { DebugRenderer } from "../utils/debugRenderer";
import { TrenchioSprite, toPixiCoords } from "../utils/pixi";
import { t } from "../utils/translations/phrases";
import { translate } from "../utils/translations/translations";
import { PASSAGE_CLIMB_MS } from "@common/definitions/roofObstacles";
import { type OctoData } from "@common/definitions/octo";
import { type TranslationKeys } from "../utils/translations/typings";
import { type Tween } from "../utils/tween";
import type { Building } from "./building";
import { GameObject } from "./gameObject";
import { Loot } from "./loot";
import { Obstacle } from "./obstacle";
import type { Projectile } from "./projectile";

/** a helmet is drawn a little see-through, so the design on the uniform under it still shows */
const HELMET_ALPHA = 0.8;
/**
 * a long ladder's climb on the page (0.98.1): a rung's time (a hand's reach, a clank), and how near its passage a
 * climber is found on it (they take hold within reach of its foot or its top, and hang on it there)
 */
const LADDER_RUNG_MS = 560;
const LADDER_NEAR = 10;

export class Player extends GameObject.derive(ObjectCategory.Player) {
    teamID!: number;

    activeItem: WeaponDefinition = Loots.fromString("fists");

    // for common code
    get activeItemDefinition(): WeaponDefinition {
        return this.activeItem;
    }

    private _meleeSoundTimeoutID?: number;

    meleeStopSound?: GameSound;
    meleeAttackCounter = 0;

    bushID?: number;

    backEquippedMelee?: MeleeDefinition;

    private activeDisguise?: ObstacleDefinition;
    halloweenThrowableSkin = false;

    infected = false;

    hasBubble = false;

    hasMagneticField = false;

    emitLowHealthParticles = false;

    isCycling = false;

    activeOverdrive = false;
    overdriveCooldown?: Timeout;

    private _oldItem = this.activeItem;

    equipment: {
        helmet?: ArmorDefinition
        vest?: ArmorDefinition
        mask?: ArmorDefinition
        backpack: BackpackDefinition
    } = {
        backpack: Loots.fromString("bag")
    };

    distTraveled = 0;

    get isActivePlayer(): boolean {
        return this.id === Game.activePlayerID;
    }

    footstepSound?: GameSound;
    actionSound?: GameSound;

    action = {
        type: PlayerActions.None,
        item: undefined as undefined | HealingItemDefinition
    };

    animation = AnimationType.None;
    animationChangeTime = 0;

    damageable = true;

    hideEquipment = false;

    downed = false;
    beingRevived = false;
    bleedEffectInterval?: NodeJS.Timeout;

    private _skin: ReferenceTo<SkinDefinition> = "";

    private _lastParticleTrail = Date.now();

    readonly images: {
        aimTrail?: Graphics
        altAimTrail?: Graphics
        readonly vest: TrenchioSprite
        readonly body: TrenchioSprite
        readonly emblem: TrenchioSprite
        readonly leftFist: TrenchioSprite
        readonly rightFist: TrenchioSprite
        leftLeg?: TrenchioSprite
        rightLeg?: TrenchioSprite
        readonly backpack: TrenchioSprite
        /** a gas mask, over the face (under the helmet's rim) */
        readonly mask: TrenchioSprite
        readonly helmet: TrenchioSprite
        readonly weapon: TrenchioSprite
        readonly altWeapon: TrenchioSprite
        readonly muzzleFlash: TrenchioSprite
        readonly waterOverlay: TrenchioSprite
        readonly blood: Container
        readonly backMeleeSprite: TrenchioSprite
        bubbleSprite?: TrenchioSprite
        disguiseSprite?: TrenchioSprite
        disguiseSpriteTrunk?: TrenchioSprite
        magneticFieldSprite?: TrenchioSprite
        healthBar?: Graphics
    };

    readonly emote: {
        image: TrenchioSprite
        background: TrenchioSprite
        container: Container
    };

    /** a voice reaction's words over the player (0.92), shown while its clip plays */
    readonly reaction: {
        text: Text
        background: Graphics
        container: Container
    };

    /** the store's picture emblem the player wears (its item id), drawn once its picture is read */
    private _storeEmblem?: string;
    /**
     * how the end screens draw this player (1.05): the skin they wear and the emblem they show (drawn, or a store
     * picture's item id), none while a skin or a disguise hides it
     */
    look?: { readonly skin: string, readonly emblem?: Emblem, readonly storeEmblem?: string };
    private _reactionHideTimeout?: Timeout;
    private _reactionSound?: GameSound;
    private _reactionClipTimeout?: ReturnType<typeof setTimeout>;
    /** the reactions shown over this player so far (a clip read late is played only for the latest) */
    private _reactionsSaid = 0;

    teammateName?: {
        text: Text
        badge?: TrenchioSprite
        container: Container
    };

    healingParticlesEmitter: ParticleEmitter;

    smokeParticlesEmitter?: ParticleEmitter;

    readonly anims: {
        emote?: Tween<ObservablePoint>
        emoteHide?: Tween<ObservablePoint>

        leftFist?: Tween<TrenchioSprite>
        rightFist?: Tween<TrenchioSprite>
        leftLeg?: Tween<TrenchioSprite>
        rightLeg?: Tween<TrenchioSprite>
        weapon?: Tween<TrenchioSprite>
        pin?: Tween<TrenchioSprite>
        muzzleFlashFade?: Tween<TrenchioSprite>
        muzzleFlashRecoil?: Tween<TrenchioSprite>
        waterOverlay?: Tween<TrenchioSprite>
        sizeMod?: Tween<ObservablePoint>
        shieldScale?: Tween<ObservablePoint>
        magneticField?: Tween<ObservablePoint>
        /** the climb through a hatch or up a ladder (1.09) */
        climb?: Tween<ObservablePoint>
        climbFade?: Tween<Container>
        /** the landing's settle to size (1.09, managers/jumpIn.ts) */
        land?: Tween<ObservablePoint>
    } = {};

    private _emoteHideTimeout?: Timeout;

    distSinceLastFootstep = 0;

    helmetLevel = NaN;
    maskLevel = NaN;
    vestLevel = NaN;
    backpackLevel = NaN;

    private _hitbox = new CircleHitbox(GameConstants.player.radius);
    get hitbox(): CircleHitbox { return this._hitbox; }

    private readonly _bulletWhizHitbox = new CircleHitbox(GameConstants.player.radius * BULLET_WHIZ_SCALE);
    get bulletWhizHitbox(): CircleHitbox { return this._bulletWhizHitbox; }

    floorType: FloorNames = FloorNames.Grass;

    sizeMod = 1;

    reloadMod = 1;

    constructor(id: number, data: ObjectsNetData[ObjectCategory.Player]) {
        super(id);

        this.images = {
            vest: new TrenchioSprite().setVisible(false),
            body: new TrenchioSprite(),
            emblem: new TrenchioSprite().setVisible(false),
            leftFist: new TrenchioSprite(),
            rightFist: new TrenchioSprite(),
            backpack: new TrenchioSprite().setVisible(false).setPos(-35, 0).setZIndex(-1),
            mask: new TrenchioSprite().setVisible(false).setPos(8, 0).setZIndex(5),
            helmet: new TrenchioSprite().setVisible(false).setPos(-8, 0).setZIndex(6).setAlpha(HELMET_ALPHA),
            weapon: new TrenchioSprite().setZIndex(3),
            altWeapon: new TrenchioSprite().setZIndex(3),
            muzzleFlash: new TrenchioSprite("muzzle_flash").setVisible(false).setZIndex(7).setAnchor(Vec(0, 0.5)),
            waterOverlay: new TrenchioSprite("water_overlay").setVisible(false).setTint(Game.colors.water),
            blood: new Container(),
            backMeleeSprite: new TrenchioSprite()
        };

        if (Game.isTeamMode) {
            const createLegImage = (): TrenchioSprite => new TrenchioSprite().setPos(-35, 26).setZIndex(-1).setScale(1.5, 0.8);
            this.images.leftLeg = createLegImage();
            this.images.rightLeg = createLegImage();
        }

        if (InputManager.isMobile && this.isActivePlayer && !Game.spectating) {
            const ctx = new GraphicsContext();
            for (let i = 0; i < 100; i++) {
                ctx.circle((i * 50) + 20, 0, 8).fill({ color: 0xffffff, alpha: 0.35 });
            }

            const aimTrail = this.images.aimTrail = new Graphics(ctx);
            const altAimTrail = this.images.altAimTrail = new Graphics(ctx);

            aimTrail.visible = false;
            altAimTrail.visible = false;

            this.container.addChild(aimTrail, altAimTrail);
        }

        this.container.addChild(
            this.images.vest,
            this.images.body,
            this.images.emblem,
            this.images.leftFist,
            this.images.rightFist,
            ...(Game.isTeamMode ? [this.images.leftLeg, this.images.rightLeg] as readonly TrenchioSprite[] : []),
            this.images.backpack,
            this.images.mask,
            this.images.helmet,
            this.images.weapon,
            this.images.altWeapon,
            this.images.muzzleFlash,
            this.images.waterOverlay,
            this.images.blood,
            this.images.backMeleeSprite
        );

        // sleeves, glove details, a contact shadow, mud and kill chevrons: all around the circle, never on it
        this._gear = new PlayerGear(this.container, this.images.leftFist, this.images.rightFist);
        this.container.sortableChildren = true;
        this.container.sortChildren();

        this.container.zIndex = ZIndexes.Players;

        const emote = this.emote = {
            background: new TrenchioSprite("emote_background").setPos(0, 0),
            image: new TrenchioSprite().setPos(0, 0),
            container: new Container()
        };

        emote.container.addChild(emote.background, emote.image);
        emote.container.zIndex = ZIndexes.Emotes;
        emote.container.visible = false;
        this.containers.push(emote.container);

        const reaction = this.reaction = {
            text: new Text({
                text: "",
                style: { fill: 0xf4efe4, fontSize: 30, fontFamily: "Inter", fontWeight: "700", align: "center" }
            }),
            background: new Graphics(),
            container: new Container()
        };
        reaction.text.anchor.set(0.5);
        reaction.container.addChild(reaction.background, reaction.text);
        reaction.container.zIndex = ZIndexes.Emotes;
        reaction.container.visible = false;
        this.containers.push(reaction.container);

        this.updateFistsPosition(false);
        this.updateWeapon();

        this.healingParticlesEmitter = ParticleManager.addEmitter({
            delay: 350,
            active: false,
            spawnOptions: () => {
                let frame = "";
                if (this.action.item?.defType === DefinitionType.HealingItem) {
                    const item = this.action.item;
                    const restores = item.effect?.restoreAmounts;
                    if (item.healType !== HealType.Special) {
                        frame = HealType[item.healType].toLowerCase();
                    } else if (item.idString === "vaccine_syringe" || !restores?.length) {
                        frame = item.idString.toLowerCase(); // has its own particle
                    } else {
                        // e.g. Field Meat: alternate health and adrenaline particles, as it restores both
                        frame = HealType[restores[Math.floor(Math.random() * restores.length)].healType].toLowerCase();
                    }
                }

                return {
                    frames: `${frame}_particle`,
                    position: this._hitbox.randomPoint(),
                    lifetime: 1000,
                    zIndex: ZIndexes.Players,
                    layer: this.layer,
                    rotation: 0,
                    alpha: {
                        start: 1,
                        end: 0
                    },
                    scale: {
                        start: 1,
                        end: 1.5
                    },
                    speed: Vec(randomFloat(-1, 1), -3)
                };
            }
        });

        this.images.body.eventMode = "static";
        this.images.body.on("pointerdown", (): void => {
            if (!Game.spectating || Game.activePlayerID === this.id) return;
            Game.sendPacket(
                SpectatePacket.create({
                    spectateAction: SpectateActions.SpectateSpecific,
                    playerID: this.id
                })
            );
        });

        this.updateFromData(data, true);
    }

    override updateContainerPosition(): void {
        super.updateContainerPosition();

        if (this.destroyed) return;

        // (0.98.8: set in place, as Vec.addComponent's sums: four vectors a player a frame no more)
        const { x, y } = this.container.position;
        this.emote.container.position.set(x + 0, y + -175);
        this.reaction.container.position.set(x + 0, y + -130);
        this.teammateName?.container.position.set(x + 0, y + 95);
        this.images.healthBar?.position.set(x + 0, y + -90);

        if (this.disguiseContainer) this.disguiseContainer.position.copyFrom(this.container.position);
    }

    override update(): void {
        // (on a long ladder, every frame, smoothed or not: 0.98.1)
        if (this._ladder) this._ladderPose();
    }

    override updateInterpolation(): void {
        this.updateContainerPosition();
        if (!this.isActivePlayer || !GameConsole.getBuiltInCVar("cv_responsive_rotation") || Game.spectating) {
            this.updateContainerRotation();
        }
        // (on a long ladder: facing the wall whatever they aim at, the hands on the rungs, before the gear follows them)
        if (this._ladder) this._ladderPose();
        // in the air (1.06): drawn by the jump-in each frame (JumpInView.update), smoothed or not; no aim trail up there
        if (this.air) {
            if (this.images.aimTrail) this.images.aimTrail.visible = false;
            if (this.images.altAimTrail) this.images.altAimTrail.visible = false;
        }
        this._gear.update({
            position: this.position,
            rotation: this.container.rotation,
            visible: !this.dead && this.images.body.visible && this.activeDisguise === undefined,
            inWater: this.images.waterOverlay.visible,
            leftFist: this.images.leftFist,
            rightFist: this.images.rightFist,
            // (up there, no shadow at their feet, the chute's is on the ground, and no mud from the flight)
            ...(this.air ? { shadow: 0, airborne: true } : {})
        });
    }

    /** in the air (1.06), kept over every floor (managers/jumpIn.ts places them): a change of floor's move leaves them there */
    override updateLayer(forceUpdate = false): void {
        super.updateLayer(forceUpdate);
        if (this.air && !this.air.aboard && this.container.parent !== CameraManager.container) CameraManager.addObject(this.container);
    }

    /** down from the air (1.06): back in their floor's container, facing as they aim */
    restoreAfterAir(): void {
        this.updateLayer(true);
        this.container.rotation = this.rotation;
        this.container.zIndex = this.downed ? ZIndexes.DownedPlayers : ZIndexes.Players;
    }

    /** sleeves, gloves, shadow, mud and chevrons (see playerGear.ts) */
    private readonly _gear: PlayerGear;
    get gear(): PlayerGear { return this._gear; }

    /** the Training Range's straw dummies don't bleed (a hit raises dust) */
    get bloodless(): boolean { return this._skin === "training_dummy"; }
    /** skins whose look blood on the uniform would spoil (blood still flies and lands) */
    get skinHidesBlood(): boolean { return this._skin !== "" && Skins.reify(this._skin).hideBlood === true; }
    get disguised(): boolean { return this.activeDisguise !== undefined; }

    /** they wear armour others can see (a skin or disguise hiding their gear hides it) */
    get armorShows(): boolean {
        return this.armorStruck(() => 0) !== undefined;
    }

    /**
     * What a bullet striking this player struck of their armour, if they wear any that shows: their helmet or their
     * vest (both worn: the vest, the bigger, more often). Armour takes part of every hit wherever it lands (the
     * server's rule), so which one rang is only for the sound and the sparks. A skin or disguise that hides the gear
     * hides this too: its sparks would tell what can't be seen.
     */
    armorStruck(random: () => number = Math.random): "helmet" | "vest" | undefined {
        if (this.hideEquipment) return undefined;
        // (a werewolf's fur is a vest in the definitions, but no steel: it neither rings nor sparks)
        const helmet = this.helmetLevel > 0, vest = this.vestLevel > 0 && this.equipment.vest?.idString !== "werewolf_fur";
        if (helmet && vest) return random() < 0.4 ? "helmet" : "vest";
        return helmet ? "helmet" : vest ? "vest" : undefined;
    }

    spawnCasingParticles(filterBy: "fire" | "reload" | "cycle", altFire = false): void {
        const weaponDef = this.activeItem as GunDefinition;
        const reference = this._getItemReference() as SingleGunNarrowing;
        const initialRotation = this.rotation + Math.PI / 2;
        const casings = reference.casingParticles?.filter(c => (c.on ?? "fire") === filterBy) as NonNullable<SingleGunNarrowing["casingParticles"]>;

        if (casings?.length === 0 || reference.casingParticles === undefined) return;

        for (const casingSpec of casings) {
            const position = Vec.scale(casingSpec.position, this.sizeMod);
            if (weaponDef.isDual) {
                position.y = (altFire ? -1 : 1) * (position.y + weaponDef.leftRightOffset);
            }

            const spawnCasings = (): void => {
                const casingVelX = casingSpec.velocity?.x;
                const casingVelY = casingSpec.velocity?.y;
                const frame = casingSpec.frame ?? Ammos.fromString(weaponDef.ammoType).defaultCasingFrame ?? "";

                for (let i = 0; i < (casingSpec.count ?? 1); i++) {
                    const spinAmount = randomFloat(Math.PI / 2, Math.PI);
                    const displacement = Vec.scale(
                        randomVector(
                            casingVelX?.min ?? 2,
                            casingVelX?.max ?? -5,
                            casingVelY?.min ?? 10,
                            casingVelY?.max ?? 15
                        ), this.sizeMod
                    );

                    if (casingVelX?.randomSign) {
                        displacement.x *= randomSign();
                    }

                    if (casingVelY?.randomSign) {
                        displacement.y *= randomSign();
                    }

                    // make casings spin clockwise or counterclockwise depending on which way they're flying
                    const spin = (Math.sign(displacement.y) || (randomSign() * randomFloat(0, 0.3))) * spinAmount;
                    const start = Vec.add(this.position, Vec.rotate(position, this.rotation));
                    const speed = Vec.rotate(Vec.addComponent(displacement, -(spinAmount / 4), 0), this.rotation);

                    // thrown, bouncing and lying on the ground a while, ringing as they land (managers/combatFx.ts);
                    // with the effects off, the plain ones that fly out and fade
                    if (frame && CombatFx.casing(frame, start, speed, initialRotation, spin, this.layer, this.sizeMod, weaponDef, filterBy !== "reload")) continue;

                    ParticleManager.spawnParticle({
                        frames: frame,
                        zIndex: ZIndexes.Players - 0.5,
                        position: start,
                        lifetime: 400,
                        layer: this.layer,
                        scale: {
                            start: 0.8 * this.sizeMod,
                            end: 0.4 * this.sizeMod
                        },
                        alpha: {
                            start: 1,
                            end: 0,
                            ease: EaseFunctions.sexticIn
                        },
                        rotation: {
                            start: initialRotation,
                            end: initialRotation + spin
                        },
                        speed
                    } satisfies ParticleOptions);
                }
            };

            if (!casingSpec.ejectionDelay) {
                spawnCasings();
            } else {
                const reference = weaponDef.idString;
                this.addTimeout(
                    () => {
                        if (reference !== this.activeItem.idString) return;

                        spawnCasings();
                    },
                    casingSpec.ejectionDelay
                );
            }
        }
    }

    /** the Gravity Gloves' beam (drawn by GravityFx), while there is one */
    beam?: GravityBeamData;

    /** this player's flashlight is on, as the server says (1.05: at night its beam shows, managers/nightLight.ts) */
    flashlightOn = true;

    /**
     * in the air (1.06, jumping in: managers/jumpIn.ts): how high (m), and whether still in a plane, as the server says;
     * braking (1.09: the jump key held, the canopy flared)
     */
    air?: { readonly height: number, readonly aboard: boolean, readonly braking?: boolean, readonly tossed?: boolean };

    /** climbing a ladder or through a hatch (1.09, the roofs' passages), as the server says: drawn climbing */
    climbing = false;

    /** the Octo Gadget's four arms, as the server last sent them (1.10, definitions/octo.ts); undefined without the gadget */
    octoArms?: OctoData;

    /**
     * on a long ladder (0.98.1, the blocks' ladder from the ground to the roof): when they took hold of it (the page's
     * clock), how long the climb takes, the way to the wall they face, the rungs gone by (a clank each), their hands'
     * sizes as they took hold
     */
    private _ladder?: { readonly start: number, readonly ms: number, readonly facing: number, rung: number, readonly fists: readonly [number, number] };

    /**
     * the long ladder they're climbing (0.98.1): the passage with a climb of its own nearest them (they took hold of it
     * within reach of it), its time and the way they face on it (to the wall: its picture's top, turned with it)
     */
    private _ladderHere(): { readonly ms: number, readonly facing: number } | undefined {
        let found: { readonly ms: number, readonly facing: number } | undefined;
        let nearest = LADDER_NEAR * LADDER_NEAR;
        for (const obstacle of Game.objects.getCategory(ObjectCategory.Obstacle)) {
            const way = obstacle.definition.passage;
            if (way?.climbMs === undefined || way.ladder === undefined) continue;
            const distance = Geometry.distanceSquared(obstacle.position, this.position);
            if (distance >= nearest) continue;
            nearest = distance;
            const wall = Vec.addAdjust(Vec(0, 0), Vec(0, -1), obstacle.orientation);
            found = { ms: way.climbMs, facing: Math.atan2(wall.y, wall.x) };
        }
        return found;
    }

    /**
     * onto a long ladder (0.98.1, the owner: "add a ladder but it takes 8 seconds to climb al lthe way down/up, animate
     * it well"), `elapsed` into its climb: drawn whole (not shrunk into a hatch), facing the wall, the gun slung, the
     * hands taking the rungs in turn (`_ladderPose`, every frame); the own player's bar says Climbing... all the way
     */
    private _onLadder(ladder: { readonly ms: number, readonly facing: number }, elapsed: number): void {
        this.anims.climb?.kill();
        this.anims.climbFade?.kill();
        this.container.alpha = 1;
        this.container.scale.set(this.sizeMod);
        this._ladder = {
            start: Date.now() - elapsed,
            ms: ladder.ms,
            facing: ladder.facing,
            rung: Math.floor(elapsed / LADDER_RUNG_MS),
            fists: [this.images.leftFist.scale.x, this.images.rightFist.scale.x]
        };
        this.playSound("metal_step_1", { falloff: 0.6, maxRange: 48 });
        this._ladderPose();
        if (this.isActivePlayer) UIManager.animateAction(translate("action_climbing"), Math.max(0, ladder.ms - elapsed) / 1000);
    }

    /**
     * a frame on a long ladder (0.98.1): facing the wall, swaying a little with each pull; the hands up the rails in
     * turn, the one reaching up further along the wall and a little bigger (nearer the eye, seen from above); the whole
     * of them a little bigger halfway up, high over the ground; a clank of the rungs every `LADDER_RUNG_MS`
     */
    private _ladderPose(): void {
        const ladder = this._ladder;
        if (!ladder || this.destroyed) return;
        const t = Date.now() - ladder.start;
        const p = Numeric.clamp(t / ladder.ms, 0, 1);
        const phase = (t / LADDER_RUNG_MS) * Math.PI;
        const reach = Math.sin(phase);
        // (nothing in the hands: whatever they held, or swapped to on the rungs, slung)
        this.anims.leftFist?.kill();
        this.anims.rightFist?.kill();
        this.anims.weapon?.kill();
        this.images.weapon.setVisible(false);
        this.images.altWeapon.setVisible(false);
        this.images.muzzleFlash.setVisible(false);
        this.images.leftFist.setPos(41 + 10 * reach, -29);
        this.images.rightFist.setPos(41 - 10 * reach, 29);
        this.images.leftFist.scale.set(ladder.fists[0] * (1 + 0.12 * reach));
        this.images.rightFist.scale.set(ladder.fists[1] * (1 - 0.12 * reach));
        this.container.scale.set(this.sizeMod * (1 + 0.07 * Math.sin(Math.PI * p)));
        this.container.rotation = ladder.facing + 0.045 * Math.sin(phase);
        const rung = Math.floor(t / LADDER_RUNG_MS);
        if (rung > ladder.rung && p < 1) {
            ladder.rung = rung;
            this.playSound(rung % 2 === 1 ? "metal_step_2" : "metal_step_1", { falloff: 0.6, maxRange: 48 });
        }
    }

    /** off a long ladder (0.98.1): their hands and what they held as before, facing as they aim, back to size */
    private _offLadder(): void {
        const ladder = this._ladder;
        if (!ladder) return;
        this._ladder = undefined;
        this.images.leftFist.scale.set(ladder.fists[0]);
        this.images.rightFist.scale.set(ladder.fists[1]);
        this.container.rotation = this.rotation;
        this.anims.climb = Game.addTween({
            target: this.container.scale,
            to: { x: this.sizeMod, y: this.sizeMod },
            duration: 260,
            ease: EaseFunctions.backOut,
            onComplete: () => { this.anims.climb = undefined; }
        });
        this.updateFistsPosition(true);
        this.updateWeapon();
        if (this.isActivePlayer && UIManager.ui.actionName.text() === translate("action_climbing")) UIManager.cancelAction();
    }

    /**
     * the climb (1.09): drawn going into the hatch or up the ladder, smaller and fainter over the climb with a little
     * sway, and back to size as they come out on the other floor; the own player's action bar says Climbing... (0.98.1:
     * a long ladder's climb drawn as itself, `_onLadder`)
     */
    private _climbAnimation(climbing: boolean): void {
        if (!climbing && this._ladder) {
            this._offLadder();
            return;
        }
        const ladder = climbing ? this._ladderHere() : undefined;
        if (ladder) {
            this._onLadder(ladder, 0);
            return;
        }
        this.anims.climb?.kill();
        this.anims.climbFade?.kill();
        const size = this.sizeMod;
        if (climbing) {
            this.anims.climb = Game.addTween({
                target: this.container.scale,
                to: { x: size * 0.7, y: size * 0.7 },
                duration: PASSAGE_CLIMB_MS,
                ease: EaseFunctions.sineIn,
                onComplete: () => { this.anims.climb = undefined; }
            });
            this.anims.climbFade = Game.addTween({
                target: this.container,
                to: { alpha: 0.35 },
                duration: PASSAGE_CLIMB_MS,
                ease: EaseFunctions.sineIn,
                onComplete: () => { this.anims.climbFade = undefined; }
            });
            // the rungs under the boots
            this.playSound("metal_step_1", { falloff: 0.6, maxRange: 48 });
            Game.addTimeout(() => { if (this.climbing && !this.destroyed) this.playSound("metal_step_2", { falloff: 0.6, maxRange: 48 }); }, PASSAGE_CLIMB_MS * 0.5);
            if (this.isActivePlayer) UIManager.animateAction(translate("action_climbing"), PASSAGE_CLIMB_MS / 1000);
        } else {
            // out the other end: up to size again with a little bounce
            this.container.scale.set(size * 0.7);
            this.container.alpha = 0.35;
            this.anims.climb = Game.addTween({
                target: this.container.scale,
                to: { x: size, y: size },
                duration: 260,
                ease: EaseFunctions.backOut,
                onComplete: () => { this.anims.climb = undefined; }
            });
            this.anims.climbFade = Game.addTween({
                target: this.container,
                to: { alpha: 1 },
                duration: 200,
                ease: EaseFunctions.sineOut,
                onComplete: () => { this.anims.climbFade = undefined; }
            });
            // (the climb's own bar, if it's still up: never a reload's or a bandage's started as the climb ended)
            if (this.isActivePlayer && UIManager.ui.actionName.text() === translate("action_climbing")) UIManager.cancelAction();
        }
    }

    /**
     * a climber in view from the start (1.09): drawn as far into the climb as they are, so the climb's end brings them
     * out as everyone else's (never full size on the rungs, then a snap down and a bounce back)
     */
    private _midClimb(): void {
        // (a long ladder's climber: on it, halfway as far as the page can tell, 0.98.1)
        const ladder = this._ladderHere();
        if (ladder) {
            this._onLadder(ladder, ladder.ms / 2);
            return;
        }
        const size = this.sizeMod;
        this.container.scale.set(size * 0.7);
        this.container.alpha = 0.35;
    }

    override updateFromData(data: ObjectsNetData[ObjectCategory.Player], isNew = false): void {
        // the Gravity Gloves' beam: every update says whether there is one
        GravityFx.setBeam(this, data.beam);
        this.beam = data.beam;
        // the Octo Gadget's arms (1.10): on their back while they carry it (drawn alive by octo/octoFx.ts, told below,
        // once this update's floor is theirs)
        this.octoArms = data.octo;
        this.flashlightOn = data.flashlight !== false;
        // in the air (1.06): out of the plane, or down on the ground (a landing, where they came down: below)
        const wasAir = this.air;
        this.air = data.air;
        // (nothing hits them up there, as on the server: a bullet on the page stopped on a chute's spot, and the
        // server's flew on to whoever was behind it: 1.06's audit)
        this.damageable = data.air === undefined;
        if (wasAir?.aboard && !isNew && !data.air?.aboard) JumpInView.jumped(this);

        // Position and rotation
        const oldPosition = Vec.clone(this.position);
        this.position = data.position;
        this._hitbox.position = this.position;
        this._bulletWhizHitbox.position = this.position;
        // down on the ground (1.06): the dust where they landed, the chute folding beside them; put out up there (their
        // 30 s ran out), no landing: their chute goes with them (1.06.1's audit: a puff, a step and a chute folded)
        if (wasAir && !isNew && !data.air) {
            if (data.full?.dead === true) JumpInView.putOut(this);
            // (on the floor they came down on, a deck's: this update's, not yet applied below; 1.09's audit, the dust of
            // a landing on a deck puffed under the roof)
            else JumpInView.landed(this, data.full?.layer ?? this.layer);
        }

        this.rotation = data.rotation;

        const noMovementSmoothing = !GameConsole.getBuiltInCVar("cv_movement_smoothing");

        if (
            (
                noMovementSmoothing
                && !(this.isActivePlayer && GameConsole.getBuiltInCVar("cv_responsive_rotation"))
            ) || isNew
        ) {
            this.container.rotation = this.rotation;
        }

        if (this.isActivePlayer) {
            SoundManager.position = this.position;
            MapManager.setPosition(this.position);
            // (under a chute: how you drift, for the ring where you'll come down)
            if (this.air && !this.air.aboard) JumpInView.noteOwn(this.position);

            if (noMovementSmoothing) CameraManager.position = toPixiCoords(this.position);

            if (GameConsole.getBuiltInCVar("pf_show_pos")) {
                UIManager.ui.debugPos.html(
                    `X: ${this.position.x.toFixed(2)}<br>Y: ${this.position.y.toFixed(2)}<br>Z: ${this.layer ?? 0}`
                );
            }
        }

        // (in the air, 1.06, over the water isn't in it)
        const floorType = this.air ? FloorNames.Grass : MapManager.terrain.getFloor(this.position, this.layer);
        if (floorType !== this.floorType) {
            const doOverlay = FloorTypes[floorType].overlay;
            if (doOverlay) {
                this.images.waterOverlay.setVisible(true);
            }

            this.anims.waterOverlay?.kill();
            this.anims.waterOverlay = Game.addTween({
                target: this.images.waterOverlay,
                to: { alpha: doOverlay ? 1 : 0 },
                duration: 200,
                onComplete: () => {
                    this.anims.waterOverlay = undefined;
                    if (!doOverlay) {
                        this.images.waterOverlay.setVisible(false);
                    }
                }
            });
        }
        this.floorType = floorType;

        // (no steps in the air, 1.06)
        if (!isNew && !this.air) {
            const dist = Geometry.distance(oldPosition, this.position);
            this.distSinceLastFootstep += dist;
            this.distTraveled += dist;

            if (this.distTraveled > 8 && this.downed) {
                this.playAnimation(AnimationType.Downed);

                this.distTraveled = 0;
            }

            if (this.distSinceLastFootstep > 10) {
                const footstepFloor = FloorTypes[this.floorType].slippery ? FloorNames.Ice : this.floorType;
                this.footstepSound = this.playSound(
                    `${footstepFloor}_step_${random(1, 2)}`,
                    {
                        falloff: 0.6,
                        maxRange: 48
                    }
                );

                this.distSinceLastFootstep = 0;

                if (FloorTypes[floorType].particles) {
                    const options = {
                        frames: "ripple_particle",
                        zIndex: ZIndexes.BuildingsFloor + 0.9,
                        position: this._hitbox.randomPoint(),
                        lifetime: 1000,
                        layer: this.layer,
                        speed: Vec(0, 0)
                    };

                    // outer
                    ParticleManager.spawnParticle({
                        ...options,
                        scale: {
                            start: randomFloat(0.45, 0.55),
                            end: randomFloat(2.95, 3.05)
                        },
                        alpha: {
                            start: randomFloat(0.55, 0.65),
                            end: 0
                        }
                    });

                    // inner
                    ParticleManager.spawnParticle({
                        ...options,
                        scale: {
                            start: randomFloat(0.15, 0.35),
                            end: randomFloat(1.45, 1.55)
                        },
                        alpha: {
                            start: randomFloat(0.25, 0.35),
                            end: 0
                        }
                    });
                }
            }
        }

        if (isNew || !GameConsole.getBuiltInCVar("cv_movement_smoothing")) {
            this.container.position.copyFrom(toPixiCoords(this.position));
            this.emote.container.position.copyFrom(Vec.addComponent(toPixiCoords(this.position), 0, -175));
            this.reaction.container.position.copyFrom(Vec.addComponent(toPixiCoords(this.position), 0, -130));
            this.teammateName?.container.position.copyFrom(Vec.addComponent(toPixiCoords(this.position), 0, 95));
            this.images.healthBar?.position.copyFrom(Vec.addComponent(toPixiCoords(this.position), 0, -90));
        }

        if (data.animation !== undefined) {
            this.animation = data.animation;
            this.animationChangeTime = Date.now();
            this.playAnimation(data.animation);
        }

        if (data.full) {
            const {
                full: {
                    layer,
                    dead,
                    downed,
                    beingRevived,
                    teamID,
                    invulnerable,
                    activeItem,
                    sizeMod,
                    reloadMod,
                    skin,
                    emblem,
                    storeEmblem,
                    helmet,
                    vest,
                    mask,
                    backpack,
                    halloweenThrowableSkin,
                    activeDisguise,
                    infected,
                    backEquippedMelee,
                    hasBubble,
                    activeOverdrive,
                    hasMagneticField,
                    isCycling,
                    emitLowHealthParticles,
                    kills
                }
            } = data;
            this._gear.kills = kills ?? 0;

            const layerChanged = layer !== this.layer;
            let oldLayer: Layer | undefined;
            if (layerChanged) {
                oldLayer = this.layer;
                this.layer = layer;
                if (!this.isActivePlayer || isNew) this.updateLayer();
            }
            if (this.isActivePlayer && (layerChanged || isNew)) {
                Game.updateLayer(layer, isNew, oldLayer);
            }

            this.backEquippedMelee = backEquippedMelee;

            this.container.visible = !dead;

            const hadSkin = this.halloweenThrowableSkin;
            if (hadSkin !== (this.halloweenThrowableSkin = halloweenThrowableSkin)) {
                if (this.activeItem.defType === DefinitionType.Throwable && !this.activeItem.noSkin) {
                    this.images.weapon.setFrame(`${this.activeItem.idString}${this.halloweenThrowableSkin ? "_halloween" : ""}`);
                }
                UIManager.updateWeaponSlots(true);
            }

            // the body (Settings → Graphics → Blood & gore): remembered now, while the gear they die in is still on
            if (!isNew && !this.dead && dead) GoreManager.playerDied(this);
            else if (isNew && dead) GoreManager.playerSeenDead(this);

            this.dead = dead;

            this.teamID = teamID;

            void Game.fontObserver.then(() => this.updateTeammateName());

            // (not while a climb fades them: a full update mid-climb flashed them whole for a frame; 1.10.1's audit)
            if (!this.climbing) this.container.alpha = invulnerable && !this.air ? 0.5 : 1;

            if (this.downed !== downed) {
                this.downed = downed;
                this.container.zIndex = this.downed ? ZIndexes.DownedPlayers : ZIndexes.Players;
                this.updateFistsPosition(false);
                this.updateWeapon(isNew);
                this.updateEquipment();
            }

            if (this.beingRevived !== beingRevived) {
                this.beingRevived = beingRevived;

                if (this.isActivePlayer) {
                    // somewhat an abuse of that system, but dedicating an
                    // entire "action" to this would be wasteful
                    if (this.beingRevived) {
                        UIManager.animateAction(translate("action_being_revived"), GameConstants.player.reviveTime, true);
                    } else {
                        UIManager.cancelAction();
                    }
                }
            }

            if (this.downed && !this.beingRevived && !this.bleedEffectInterval) {
                const bleed = (): void => this.hitEffect(this.position, randomRotation(), "bleed");
                bleed();
                this.bleedEffectInterval = setInterval(bleed, 1000);
            }

            if (this.dead || this.beingRevived) {
                clearInterval(this.bleedEffectInterval);
                this.bleedEffectInterval = undefined;
            }

            if (this.dead) {
                if (this.teammateName) this.teammateName.container.visible = false;
                if (this.images.healthBar) {
                    this.healthBarFadeTimeout?.kill();
                    this.healthBarTween?.kill();
                    this.images.healthBar.destroy();
                    this.images.healthBar = undefined;
                }

                if (this.disguiseContainer) this.disguiseContainer.visible = false;
                if (this.smokeParticlesEmitter !== undefined) this.smokeParticlesEmitter.active = false;
            }

            this._oldItem = this.activeItem;
            const itemDirty = this.activeItem !== activeItem;
            this.activeItem = activeItem;

            // a picture emblem from the store (0.92), or the player-drawn emblem, on top of the uniform. None while a skin or
            // a disguise hides the gear (the Ghillie Suit's camouflage, as its armour is hidden): a picture on a bush
            // gave the player away (0.94 audit)
            const hidesEmblem = activeDisguise !== undefined || skin.hideEquipment === true;
            this._storeEmblem = hidesEmblem ? undefined : storeEmblem;
            if (hidesEmblem) {
                this.images.emblem.visible = false;
            } else if (storeEmblem) {
                void storeEmblemTexture(storeEmblem).then(texture => {
                    // (still the same picture, and the player still there, by the time it's read)
                    if (!texture || this.destroyed || this._storeEmblem !== storeEmblem) return;
                    const emblemSprite = this.images.emblem;
                    emblemSprite.texture = texture;
                    emblemSprite.width = emblemSprite.height = 86;
                    emblemSprite.setZIndex(this.images.body.zIndex);
                    emblemSprite.visible = true;
                    this.container.sortChildren();
                });
            } else if (emblem) {
                const emblemSprite = this.images.emblem;
                emblemSprite.texture = emblemTexture(emblem);
                emblemSprite.width = emblemSprite.height = 86;
                emblemSprite.setZIndex(this.images.body.zIndex);
                emblemSprite.visible = true;
                this.container.sortChildren();
            } else {
                this.images.emblem.visible = false;
            }

            const skinID = skin.idString;
            const skinDef = Loots.fromString<SkinDefinition>(skinID);

            if (this.isActivePlayer) {
                const oldSkinID = UIManager.skinID;
                UIManager.skinID = skinID;

                if (oldSkinID !== undefined && oldSkinID !== skinID) {
                    UIManager.weaponCache[2] = undefined; // invalidate melee cache so fists in inventory update
                    UIManager.updateWeapons();

                    // The spectating check is to ensure that the sound is not played during spectating, specifically during player switching.
                    if (skinDef.sound && !Game.spectating) SoundManager.play(skinDef.idString);
                }
            }
            this._skin = skinID;
            this.look = { skin: skinID, emblem: hidesEmblem ? undefined : emblem, storeEmblem: hidesEmblem ? undefined : storeEmblem };

            let baseTint: ColorSource;
            let fistTint: ColorSource;
            if (skinDef.grassTint) {
                baseTint = fistTint = Game.colors.ghillie;
            } else {
                baseTint = skinDef.baseTint ?? 0xffffff;
                fistTint = skinDef.fistTint ?? 0xffffff;
            }

            const { body, leftFist, rightFist, leftLeg, rightLeg } = this.images;
            const baseFrame = skinDef.baseImage ?? `${skinID}_base`;
            const fistFrame = skinDef.fistImage ?? `${skinID}_fist`;

            body
                .setFrame(baseFrame)
                .setTint(baseTint);
            leftFist
                .setFrame(fistFrame)
                .setTint(fistTint);
            rightFist
                .setFrame(fistFrame)
                .setTint(fistTint);
            leftLeg
                ?.setFrame(fistFrame)
                .setTint(fistTint);
            rightLeg
                ?.setFrame(fistFrame)
                .setTint(fistTint);
            this._gear.setSkin(skinDef, fistTint);

            if (this.sizeMod !== sizeMod) {
                // reset the size modifier before tweening
                this.sizeMod = GameConstants.player.defaultModifiers().size;
                this._hitbox = new CircleHitbox(GameConstants.player.radius * this.sizeMod, this._hitbox.position);

                this.anims.sizeMod?.kill();
                this.anims.sizeMod = Game.addTween({
                    target: this.container.scale,
                    to: { x: sizeMod, y: sizeMod },
                    duration: 300,
                    ease: EaseFunctions.backOut,
                    onComplete: () => {
                        this.anims.sizeMod = undefined;
                        this.sizeMod = sizeMod;
                        this._hitbox = new CircleHitbox(GameConstants.player.radius * sizeMod, this._hitbox.position);
                    }
                });
            }

            if (reloadMod !== undefined && this.reloadMod !== reloadMod) this.reloadMod = reloadMod;

            const { hideEquipment, helmetLevel, vestLevel, maskLevel, backpackLevel } = this;

            this.hideEquipment = ((activeDisguise !== undefined ? true : undefined) ?? skinDef.hideEquipment) ?? false;

            this.helmetLevel = (this.equipment.helmet = helmet)?.level ?? 0;
            this.vestLevel = (this.equipment.vest = vest)?.level ?? 0;
            this.maskLevel = (this.equipment.mask = mask)?.level ?? 0;
            this.backpackLevel = (this.equipment.backpack = backpack).level;

            const backpackTint = skinDef.backpackTint
                ?? this.equipment.backpack?.defaultTint
                ?? 0xffffff;

            this.images.backpack.setTint(!this.equipment.backpack.noTint ? backpackTint : 0xffffff);

            if (
                hideEquipment !== this.hideEquipment
                || helmetLevel !== this.helmetLevel
                || vestLevel !== this.vestLevel
                || maskLevel !== this.maskLevel
                || backpackLevel !== this.backpackLevel
            ) {
                this.updateEquipment();

                if (
                    !isNew
                    && this.equipment.vest?.emitSound !== undefined
                    && vestLevel !== this.vestLevel
                ) this.playSound(this.equipment.vest.emitSound);
            }

            if (itemDirty) {
                this.updateFistsPosition(true);
                this.updateWeapon(isNew);
            }

            if (this.activeDisguise !== activeDisguise) {
                const disguiseContainer = this.disguiseContainer;

                const def = this.activeDisguise = activeDisguise;
                let disguiseSprite = this.images.disguiseSprite,
                    trunkSprite = this.images.disguiseSpriteTrunk;

                if (def !== undefined && disguiseContainer) {
                    if (!disguiseSprite) {
                        disguiseSprite = this.images.disguiseSprite = new TrenchioSprite();
                        disguiseSprite.zIndex = 999; // this only applies within the player container, not globally
                        disguiseContainer.zIndex = PLAYER_PARTICLE_ZINDEX;
                        disguiseContainer.addChild(disguiseSprite);
                    }

                    let frame = `${def.frames?.base ?? def.idString}${def.variations !== undefined ? `_${random(1, def.variations)}` : ""}`;

                    if (def.isTree) {
                        const trunkFrame = `${def.frames?.base ?? def.idString}${def.trunkVariations !== undefined && def.trunkVariations !== 1 ? `_${random(1, def.trunkVariations)}` : ""}`;
                        const leavesFrame = `${def.frames?.leaves}${def.leavesVariations !== undefined && def.leavesVariations !== 1 ? `_${random(1, def.leavesVariations)}` : ""}`;

                        if (!trunkSprite) {
                            trunkSprite = this.images.disguiseSpriteTrunk = new TrenchioSprite();
                            trunkSprite.zIndex = 998; // meow
                            disguiseContainer.addChild(trunkSprite);
                        }

                        trunkSprite.setFrame(trunkFrame);
                        frame = leavesFrame;
                    }

                    disguiseSprite.setFrame(frame);
                    if (!this.dead) disguiseContainer.visible = true;
                    trunkSprite?.setVisible(def.isTree ?? false);
                } else if (disguiseContainer) {
                    disguiseContainer.visible = false;
                }
            }

            if (!this.dead && emitLowHealthParticles !== this.emitLowHealthParticles) {
                this.emitLowHealthParticles = emitLowHealthParticles;

                if (this.smokeParticlesEmitter === undefined) {
                    this.smokeParticlesEmitter = ParticleManager.addEmitter({
                        delay: 300,
                        active: !this.dead,
                        spawnOptions: () => ({
                            frames: "smoke_particle",
                            position: this.position,
                            layer: this.layer,
                            zIndex: PLAYER_PARTICLE_ZINDEX,
                            lifetime: 3500,
                            scale: { start: 0, end: randomFloat(4, 5) },
                            alpha: { start: 0.9, end: 0 },
                            speed: Vec.fromPolar(randomFloat(-1.9, -2.1), randomFloat(5, 6))
                        })
                    });
                }
                else {
                    this.smokeParticlesEmitter.active = !this.dead && this.emitLowHealthParticles;
                }
            }

            if (infected !== this.infected) {
                this.infected = infected;
                this.container.tint = infected ? 0x8a4c70 : 0xffffff;
                if (!isNew && Game.modeName === "infection") {
                    if (infected) this.playSound("infected");
                    else this.playSound("cured");
                }
            }

            // Shield
            if (hasBubble !== this.hasBubble) {
                this.hasBubble = hasBubble;

                let bubbleSprite = this.images.bubbleSprite;

                // Initialize sprite
                if (bubbleSprite === undefined) {
                    bubbleSprite = this.images.bubbleSprite = new TrenchioSprite()
                        .setFrame("shield")
                        .setZIndex(7);

                    this.container.addChild(bubbleSprite);
                }

                bubbleSprite.setVisible(this.hasBubble);

                if (!isNew) {
                    if (!hasBubble) {
                        const perkDef = PerkData[PerkIds.ExperimentalForcefield];
                        this.playSound(perkDef.shieldDestroySound);
                        ParticleManager.spawnParticles(10, () => ({
                            frames: perkDef.shieldParticle,
                            position: this.hitbox.randomPoint(),
                            layer: this.layer,
                            zIndex: PLAYER_PARTICLE_ZINDEX,
                            lifetime: 1500,
                            rotation: {
                                start: randomRotation(),
                                end: randomRotation()
                            },
                            scale: {
                                start: randomFloat(0.85, 0.95),
                                end: 0,
                                ease: EaseFunctions.quarticIn
                            },
                            alpha: {
                                start: 1,
                                end: 0,
                                ease: EaseFunctions.sexticIn
                            },
                            speed: Vec.fromPolar(randomRotation(), randomFloat(4, 9))
                        }));
                    } else {
                        this.anims.shieldScale?.kill();
                        this.playSound(PerkData[PerkIds.ExperimentalForcefield].shieldObtainSound);

                        if (this.images.bubbleSprite !== undefined) {
                            this.images.bubbleSprite.setScale(0.25);
                            this.anims.shieldScale = Game.addTween({
                                target: this.images.bubbleSprite.scale,
                                to: { x: 1, y: 1 },
                                duration: 800,
                                ease: EaseFunctions.backOut,
                                onComplete: () => {
                                    this.anims.shieldScale = undefined;
                                }
                            });
                        }
                    }
                }
            }

            // Magnetic Field
            if (hasMagneticField !== this.hasMagneticField) {
                this.hasMagneticField = hasMagneticField;

                let magneticFieldSprite = this.images.magneticFieldSprite;

                const spriteScale = PerkData[PerkIds.EternalMagnetism].spriteScale;

                // Initialize sprite
                if (magneticFieldSprite === undefined) {
                    magneticFieldSprite = this.images.magneticFieldSprite = new TrenchioSprite()
                        .setFrame("_glow_")
                        .setScale(spriteScale)
                        .setAlpha(0.75)
                        .setZIndex(-1.2)
                        .setTint(0xfc0303);

                    this.container.addChild(magneticFieldSprite);
                }

                magneticFieldSprite.setVisible(this.hasMagneticField);

                if (!isNew) {
                    if (this.images.magneticFieldSprite !== undefined) {
                        this.anims.magneticField?.kill();
                        this.images.magneticFieldSprite.setScale(hasMagneticField ? 0 : spriteScale);

                        this.anims.magneticField = Game.addTween({
                            target: this.images.magneticFieldSprite.scale,
                            to: { x: hasMagneticField ? spriteScale : 0, y: hasMagneticField ? spriteScale : 0 },
                            duration: 1000,
                            ease: EaseFunctions.backOut,
                            onComplete: () => {
                                this.anims.magneticField = undefined;
                            }
                        });
                    }
                }
            }

            // Pan Image Display
            const backMeleeSprite = this.images.backMeleeSprite;
            const backMelee = this.backEquippedMelee;
            backMeleeSprite.setVisible(!!backMelee?.onBack);

            if (backMelee?.onBack) {
                const frame = `${backMelee.idString}${backMelee.image?.separateWorldImage ? "_world" : ""}`;
                backMeleeSprite.setFrame(frame);
                const { onBack } = backMelee;
                backMeleeSprite.setPos(onBack.position.x, onBack.position.y);
                backMeleeSprite.setAngle(onBack.angle);
            }

            // Overdrive
            if (activeOverdrive !== this.activeOverdrive) {
                this.activeOverdrive = activeOverdrive;

                this.container.tint = activeOverdrive ? 0xff0000 : 0xffffff;

                if (!isNew) {
                    const perkDef = PerkData[PerkIds.Overdrive];

                    const perkSlot = UIManager._perkSlots.find(element => {
                        return element?.attr("data-idString") === perkDef.idString;
                    });

                    const spawnParticles = (): void => {
                        if (perkSlot) perkSlot.toggleClass("deactivated");
                        ParticleManager.spawnParticles(10, () => ({
                            frames: perkDef.particle,
                            position: this.hitbox.randomPoint(),
                            layer: this.layer,
                            zIndex: PLAYER_PARTICLE_ZINDEX,
                            lifetime: 5000,
                            rotation: {
                                start: randomRotation(),
                                end: randomRotation()
                            },
                            scale: {
                                start: randomFloat(0.85, 0.95),
                                end: 0,
                                ease: EaseFunctions.quarticIn
                            },
                            alpha: {
                                start: 1,
                                end: 0,
                                ease: EaseFunctions.sexticIn
                            },
                            speed: Vec.fromPolar(randomRotation(), 5)
                        }));
                        this.playSound(perkDef.activatedSound);
                    };

                    if (activeOverdrive) {
                        spawnParticles();
                    } else {
                        this.overdriveCooldown?.kill();
                        this.overdriveCooldown = Game.addTimeout(() => {
                            spawnParticles();
                        }, perkDef.cooldown);
                    }
                }
            }

            if (isCycling !== this.isCycling) this.isCycling = isCycling;
        }

        if (data.action !== undefined) {
            const action = data.action;

            let actionSoundName = "";
            this.healingParticlesEmitter.active = false;

            this.actionSound?.stop();

            switch (action.type) {
                case PlayerActions.None: {
                    this.updateFistsPosition(false);
                    this.updateWeapon(true);

                    if (this.isActivePlayer) {
                        UIManager.cancelAction();
                    }
                    break;
                }
                case PlayerActions.Reload: {
                    const weaponDef = this.activeItem as GunDefinition;

                    if (weaponDef.isDual) {
                        this.spawnCasingParticles("reload", true);
                    }

                    this.spawnCasingParticles("reload", false);

                    const { weapons, activeWeaponIndex } = UIManager.inventory;
                    const reloadFullClip = weaponDef.reloadFullOnEmpty && (weapons[activeWeaponIndex]?.count ?? 0) <= 0;

                    actionSoundName = `${weaponDef.idString}_reload${reloadFullClip ? "_full" : ""}`;
                    if (this.isActivePlayer) {
                        UIManager.animateAction(
                            translate("action_reloading"),
                            (reloadFullClip ? weaponDef.fullReloadTime : weaponDef.reloadTime) / (this.reloadMod === 0 ? 1 : this.reloadMod)
                        );
                    }

                    break;
                }
                case PlayerActions.UseItem: {
                    const itemDef = action.item;
                    // Field Meat has no sound of its own yet: it borrows the closest healing sound
                    actionSoundName = itemDef.idString === "meat" ? "tablets" : itemDef.idString;
                    this.healingParticlesEmitter.active = true;
                    if (this.isActivePlayer) {
                        UIManager.animateAction(
                            translate(
                                `action_${itemDef.idString}_use` as TranslationKeys,
                                { item: translate(itemDef.idString as TranslationKeys) }
                            ),
                            itemDef.useTime / PerkManager.mapOrDefault(PerkIds.FieldMedic, ({ usageMod }) => usageMod, 1)
                        );
                    }
                    break;
                }
                case PlayerActions.Revive: {
                    if (this.isActivePlayer) {
                        UIManager.animateAction(
                            translate("action_reviving"),
                            GameConstants.player.reviveTime / PerkManager.mapOrDefault(PerkIds.FieldMedic, ({ usageMod }) => usageMod, 1)
                        );
                    }
                    break;
                }
            }

            if (actionSoundName) {
                let speed = action.type === PlayerActions.Reload ? this.reloadMod : 1;
                if (PerkManager.has(PerkIds.FieldMedic) && actionSoundName === action.item?.idString) {
                    speed = PerkData[PerkIds.FieldMedic].usageMod;
                }
                this.actionSound = this.playSound(
                    actionSoundName,
                    {
                        falloff: 0.6,
                        maxRange: 48,
                        speed
                    }
                );
            }

            // @ts-expect-error 'item' not existing is okay
            this.action = action;
        }

        // the Octo Gadget's arms (1.10), their floor known by now
        OctoFx.setArms(this, data.octo, isNew);

        // climbing a ladder or through a hatch (1.09), or out the other end
        if (data.climbing !== undefined && data.climbing !== this.climbing) {
            this.climbing = data.climbing;
            if (!isNew) this._climbAnimation(data.climbing);
            else if (data.climbing) this._midClimb();
        }
    }

    override updateDebugGraphics(): void {
        if (!DEBUG_CLIENT) return;
        if (!GameConsole.getBuiltInCVar("db_show_hitboxes_players")) return;

        DebugRenderer.addHitbox(this.hitbox, HITBOX_COLORS.player);
        const alpha = this.layer === Game.activePlayer?.layer ? 1 : DIFF_LAYER_HITBOX_OPACITY;

        if (this.downed) {
            DebugRenderer.addCircle(5, this.position, HITBOX_COLORS.obstacleNoCollision, alpha);
        }

        const renderMeleeReflectionSurface = (surface: { pointA: Vector, pointB: Vector }): void => {
            const start = Vec.add(
                this.position,
                Vec.rotate(surface.pointA, this.rotation)
            );

            const lineEnd = (Vec.add(
                this.position,
                Vec.rotate(surface.pointB, this.rotation)
            ));

            DebugRenderer.addLine(start, lineEnd, HITBOX_COLORS.playerWeapon, alpha);
        };

        switch (this.activeItem.defType) {
            case DefinitionType.Gun: {
                const offset = this.activeItem.bulletOffset ?? 0;

                const start = Vec.add(this.position,
                    Vec.scale(
                        Vec.rotate(Vec(0, offset), this.rotation),
                        this.sizeMod
                    ));

                DebugRenderer.addRay(
                    start,
                    this.rotation,
                    this.activeItem.length,
                    HITBOX_COLORS.playerWeapon,
                    alpha
                );
                break;
            }
            case DefinitionType.Melee: {
                const hitbox = getMeleeHitbox(this, this.activeItem);
                DebugRenderer.addHitbox(
                    hitbox,
                    HITBOX_COLORS.playerWeapon,
                    alpha
                );
                if (this.activeItem.reflectiveSurface) {
                    renderMeleeReflectionSurface(this.activeItem.reflectiveSurface);
                }

                if (this.activeItem.image?.pivot) {
                    DebugRenderer.addCircle(
                        this.sizeMod,
                        Vec.add(
                            this.position,
                            Vec.scale(
                                Vec.rotate(this.activeItem.image.pivot, this.rotation),
                                this.sizeMod
                            )
                        ),
                        HITBOX_COLORS.pivot,
                        alpha
                    );
                }

                // render raycasting debug for active player
                if (this.isActivePlayer) {
                    getMeleeTargets(hitbox, this.activeItem, this, Game.isTeamMode, Game.objects, DebugRenderer);
                }
                break;
            }
        }

        if (this.backEquippedMelee?.onBack?.reflectiveSurface) {
            renderMeleeReflectionSurface(this.backEquippedMelee?.onBack.reflectiveSurface);
        }
    }

    private _getItemReference(): SingleGunNarrowing | Exclude<WeaponDefinition, GunDefinition> {
        const weaponDef = this.activeItem;

        return weaponDef.defType === DefinitionType.Gun && weaponDef.isDual
            ? Loots.fromString<SingleGunNarrowing>(weaponDef.singleVariant)
            : weaponDef as SingleGunNarrowing | Exclude<WeaponDefinition, GunDefinition>;
    }

    private _getOffset(): number {
        const weaponDef = this.activeItem;

        return weaponDef.defType === DefinitionType.Gun && weaponDef.isDual
            ? weaponDef.leftRightOffset * PIXI_SCALE
            : 0;
    }

    updateTeammateName(): void {
        if (
            Game.isTeamMode
            && (
                !this.isActivePlayer
                && !this.teammateName
                && !this.dead
                && this.teamID === Game.teamID
            )
        ) {
            const name = Game.playerNames.get(this.id);

            const text = new Text({
                text: UIManager.getRawPlayerName(this.id),
                style: {
                    fill: name?.hasColor ? name?.nameColor : TEAMMATE_COLORS[UIManager.getTeammateColorIndex(this.id) ?? 0],
                    fontSize: 36,
                    fontFamily: "Inter",
                    fontWeight: "600",
                    dropShadow: {
                        alpha: 0.8,
                        color: "black",
                        blur: 2,
                        distance: 2
                    }
                }
            });
            const badge = name?.badge ? new TrenchioSprite(getBadgeIdString(name.badge)) : undefined;
            const container = new Container();
            this.teammateName = { text, badge, container };

            text.anchor.set(0.5);

            if (badge) {
                const oldWidth = badge.width;
                badge.width = text.height / 1.25;
                badge.height *= badge.width / oldWidth;
                badge.position = Vec(
                    text.width / 2 + 20,
                    0
                );
                container.addChild(badge);
            }

            container.addChild(text);
            container.zIndex = ZIndexes.TeammateName;
            this.containers.push(container);
            this.updateLayer(true);
        } else if (
            this.teammateName
            && (
                this.isActivePlayer
                || this.dead
                || this.teamID !== Game.teamID
            )
        ) {
            const { text, badge, container } = this.teammateName;
            text?.destroy();
            badge?.destroy();
            container?.destroy();
            this.teammateName = undefined;
            removeFrom(this.containers, container);
        }
    }

    healthBarTween?: Tween<Graphics>;
    healthBarFadeTimeout?: Timeout;

    updateHealthBar(normalizedHealth: number): void {
        this.healthBarFadeTimeout?.kill();

        let healthBar = this.images.healthBar;
        if (!healthBar) {
            healthBar = this.images.healthBar = new Graphics();
            healthBar.position = Vec.addComponent(this.container.position, 0, -90);
            healthBar.zIndex = 999;
            healthBar.alpha = 0;
            this.containers.push(healthBar);
            this.updateLayer(true);
        }

        healthBar.visible = true;
        this.healthBarTween = Game.addTween({
            target: healthBar,
            to: { alpha: 1 },
            duration: 100
        });

        this.healthBarFadeTimeout = Game.addTimeout(() => {
            if (!healthBar) return;
            this.healthBarTween = Game.addTween({
                target: healthBar,
                to: { alpha: 0 },
                duration: 100,
                onComplete: () => healthBar.visible = false
            });
        }, 100);

        healthBar
            .clear()
            .roundRect(-82.5, -17.5, 165, 35, 3)
            .fill({ color: 0x000000, alpha: 0.45 })
            .roundRect(-75, -10, 150 * normalizedHealth, 20, 2)
            .fill(UIManager.getHealthColor(normalizedHealth, this.downed));
    }

    updateFistsPosition(anim: boolean): void {
        this.anims.leftFist?.kill();
        this.anims.rightFist?.kill();
        this.anims.weapon?.kill();

        this.images.leftLeg?.setVisible(this.downed);
        this.images.rightLeg?.setVisible(this.downed);

        if (this.downed) {
            this.images.leftFist.setPos(38, -35);
            this.images.rightFist.setPos(38, 35);
            this.images.leftLeg?.setPos(-35, 26);
            this.images.rightLeg?.setPos(-35, -26);
            return;
        }

        type FistsRef = (SingleGunNarrowing | Exclude<WeaponDefinition, GunDefinition>)["fists"] & object;
        const fists: FistsRef = this.activeItemDefinition.fists ?? DEFAULT_HAND_RIGGING;

        const offset = this._getOffset();

        if (anim) {
            const duration: number = "animationDuration" in fists ? fists.animationDuration as number : 150;

            this.anims.leftFist = Game.addTween({
                target: this.images.leftFist,
                to: { x: fists.left.x, y: fists.left.y - offset },
                duration,
                onComplete: () => {
                    this.anims.leftFist = undefined;
                }
            });

            this.anims.rightFist = Game.addTween({
                target: this.images.rightFist,
                to: { x: fists.right.x, y: fists.right.y + offset },
                duration,
                onComplete: () => {
                    this.anims.rightFist = undefined;
                }
            });
        } else {
            this.images.leftFist.setPos(fists.left.x, fists.left.y - offset);
            this.images.rightFist.setPos(fists.right.x, fists.right.y + offset);
        }

        const reference = this._getItemReference();

        if (reference.image) {
            const { image: { position, angle } } = reference;

            if (reference.defType === DefinitionType.Throwable && !reference.noSkin) {
                this.images.weapon.setFrame(`${reference.idString}${this.halloweenThrowableSkin ? "_halloween" : ""}`);
            }

            this.images.weapon.setPos(position.x, position.y + offset);
            this.images.altWeapon.setPos(position.x, position.y - offset);
            this.images.weapon.setAngle(angle);
        }
    }

    updateWeapon(isNew = false): void {
        // the Gravity Gloves are worn on the hands (not drawn as a held weapon)
        GravityFx.dressHands(this, !this.downed && this.activeItem.defType === DefinitionType.Melee && this.activeItem.gravity === true);
        if (this.downed) {
            this.images.weapon.setVisible(false);
            this.images.altWeapon.setVisible(false);
            this.images.muzzleFlash.setVisible(false);
            this.images.leftFist.setZIndex(-1);
            this.images.rightFist.setZIndex(-1);
            this.container.sortChildren();
            return;
        }

        const weaponDef = this.activeItem;
        const reference = this._getItemReference();

        const { image } = reference;
        const { fists } = weaponDef;

        const imagePresent = image !== undefined;
        const isDualGun = imagePresent && weaponDef.defType === DefinitionType.Gun && weaponDef.isDual;
        if (imagePresent) {
            const usesWorldFrame = weaponDef.defType === DefinitionType.Gun
                || (image as NonNullable<MeleeDefinition["image"]>).separateWorldImage;

            let frame = reference.idString;

            if (usesWorldFrame && weaponDef.defType === DefinitionType.Gun) {
                const gunReference = reference as SingleGunNarrowing;
                const activeWeapon = this.isActivePlayer
                    ? UIManager.inventory.weapons[UIManager.inventory.activeWeaponIndex]
                    : undefined;

                const useUnloadedFrame = gunReference.image.unloadedWorldImage
                    && activeWeapon?.definition.idString === weaponDef.idString
                    && activeWeapon.count !== undefined
                    && activeWeapon.count <= 0;

                frame += useUnloadedFrame ? "_empty_world" : "_world";
            } else if (usesWorldFrame) {
                frame += "_world";
            }

            if (weaponDef.defType === DefinitionType.Throwable && this.halloweenThrowableSkin && !weaponDef.noSkin) {
                frame += "_halloween";
            }

            const { angle, position: { x: pX, y: pY } } = image;
            const offset = this._getOffset();

            this.images.weapon
                .setFrame(frame)
                .setPos(pX, pY + offset)
                .setAngle(angle)
                .setPivot(reference.image && "pivot" in reference.image && reference.image.pivot ? reference.image.pivot : Vec(0, 0));

            if (isDualGun) {
                this.images.altWeapon
                    .setFrame(frame)
                    .setPos(pX, pY - offset)
                    .setAngle(angle) // there's an ambiguity here as to whether the angle should be inverted or the same
                    .setVisible(true);

                this.images.aimTrail?.position.set(pX, pY + offset);
                this.images.altAimTrail?.position.set(pX, pY - offset);
            }
        }

        if (!isDualGun) {
            this.images.altWeapon.setVisible(false);
            this.images.aimTrail?.position.set(0, 0);
        }

        if (this.activeItem !== this._oldItem) {
            this.anims.muzzleFlashFade?.kill();
            this.anims.muzzleFlashRecoil?.kill();
            this.images.muzzleFlash.alpha = 0;
            if (this.isActivePlayer && !isNew) {
                let soundID: string;
                if (reference.defType === DefinitionType.Throwable) {
                    soundID = "throwable";
                } else if (reference.defType === DefinitionType.Gun && reference.isDual) {
                    soundID = reference.idString.slice("dual_".length);
                } else if (SoundManager.has(`${reference.idString}_switch`)) {
                    soundID = reference.idString;
                } else if (reference.defType === DefinitionType.Melee && reference.switchSound !== undefined) {
                    soundID = reference.switchSound;
                } else {
                    soundID = "default";
                }
                SoundManager.play(`${soundID}_switch`);
            }
        }

        this.images.weapon.setVisible(imagePresent);
        this.images.muzzleFlash.setVisible(imagePresent);

        switch (weaponDef.defType) {
            case DefinitionType.Gun: {
                this.images.rightFist.setZIndex((fists as SingleGunNarrowing["fists"]).rightZIndex ?? 1);
                this.images.leftFist.setZIndex((fists as SingleGunNarrowing["fists"]).leftZIndex ?? 1);
                this.images.weapon.setZIndex(image?.zIndex ?? 2);
                this.images.altWeapon.setZIndex(2);
                this.images.body.setZIndex(3);
                break;
            }
            case DefinitionType.Melee: {
                this.images.leftFist.setZIndex(4);
                this.images.rightFist.setZIndex(4);
                this.images.body.setZIndex(2);
                this.images.weapon.setZIndex(reference.image?.zIndex ?? 1);
                break;
            }
            case DefinitionType.Throwable: {
                this.images.leftFist.setZIndex(4);
                this.images.rightFist.setZIndex(4);
                this.images.body.setZIndex(2);
                this.images.weapon.setZIndex(reference.image?.zIndex ?? 5);
                break;
            }
        }

        // the emblem sits on the body: same zIndex, and it was added right after the body, so it draws on top of it
        this.images.emblem.setZIndex(this.images.body.zIndex);
        this.images.waterOverlay.setZIndex(this.images.body.zIndex + 1);
        this.container.sortChildren();
    }

    updateEquipment(): void {
        for (const item of ["helmet", "vest", "mask", "backpack"] as const) {
            this.updateEquipmentWorldImage(item);

            if (this.isActivePlayer) {
                this.updateEquipmentSlot(item);
            }
        }

        // the bag meter (a backpack changes the room) and the inventory screen's gear
        if (this.isActivePlayer) InventoryScreen.refresh();
    }

    updateEquipmentWorldImage(type: "helmet" | "vest" | "mask" | "backpack"): void {
        const image = this.images[type];
        const def = this.equipment[type];

        if (
            def
            && def.level > 0
            && !this.hideEquipment
            && ((type !== "backpack" && type !== "mask") || !this.downed)
        ) {
            if (type !== "vest") {
                image.setFrame(`${def.idString}_world`).setVisible(true);
                if (type === "helmet") image.setAlpha(HELMET_ALPHA);

                if (type === "helmet") {
                    const posOverride = (def as ArmorDefinition).positionOverride;
                    const posOverrideDowned = (def as ArmorDefinition).positionOverrideDowned;

                    image.setPos(
                        this.downed ? (posOverrideDowned ?? 10) : (posOverride ?? -8),
                        0
                    );
                }
            } else {
                const vestDef = def as ArmorDefinition & { armorType: ArmorType.Vest };
                let color: number | undefined;
                if ((color = vestDef.color) !== undefined) {
                    image.setFrame(vestDef.worldImage ?? "vest_world")
                        .setVisible(true)
                        .setTint(color);
                }
            }
        } else {
            image.setVisible(false);
        }
    }

    updateEquipmentSlot(equipmentType: "helmet" | "vest" | "mask" | "backpack"): void {
        const container = $(`#${equipmentType}-slot`);
        const def = this.equipment[equipmentType];
        TrenchHud.updateEquipment(this.equipment as never);

        if (def?.hideInHUD) {
            container.css("visibility", "hidden");
            return;
        }

        if (def && def.level > 0) {
            container.children(".item-name").text(t("Lvl. <level>", { level: def.level }));
            container.children(".item-image").attr("src", `./img/game/loot/${def.idString}.svg`);
            container.children(".item-name").attr("style", `color: ${def.level >= (Game.mode.maxEquipmentLevel ?? 3) ? "#ff9900" : "#ffffff"};`);

            let itemTooltip = translate(def.idString as TranslationKeys);
            if (def.defType === DefinitionType.Armor) {
                itemTooltip = def.armorType === ArmorType.Mask
                    ? translate("tt_gas_mask" as TranslationKeys, {
                        item: `<b>${translate(def.idString as TranslationKeys)}</b><br>`,
                        percent: (def.damageReduction * 100).toString(),
                        gas: Math.round(def.gasReduction * 100).toString()
                    })
                    : translate("tt_reduces", {
                        item: `<b>${translate(def.idString as TranslationKeys)}</b><br>`,
                        percent: (def.damageReduction * 100).toString()
                    });
            }

            container.children(".item-tooltip").html(itemTooltip);
            container.css("outline", def.noDrop || Game.spectating ? "none" : "");
        }

        container.css("visibility", (def?.level ?? 0) > 0 || UI_DEBUG_MODE ? "visible" : "hidden");

        // (a right-click on the slot drops the piece: the slot's one listener, in ui.ts. One was added here on every
        // update as well, so a click sent the drop many times over: with a spare carried it went too)

        if (equipmentType === "backpack") {
            UIManager.updateItems();
        }
    }

    canInteract(player: Player): boolean {
        return Game.isTeamMode
            && !player.downed
            && this.downed
            && !this.beingRevived
            && this !== player
            && this.teamID === player.teamID
            && adjacentOrEqualLayer(this.layer, player.layer);
    }

    /**
     * A voice reaction from the store (0.92): its clip from where the player stands (heard as far as a shout carries),
     * cut after a few seconds, and its words over the player meanwhile
     */
    showReaction(reaction: string): void {
        const saidAt = performance.now();
        // (its place among the reactions said over this player, taken as it's said: one whose item was looked up late,
        // the catalogue read again, never covers a newer one's words or clip; the review of the fix)
        const said = ++this._reactionsSaid;
        void storeReaction(reaction).then(found => {
            if (!found || this.destroyed || this._reactionsSaid !== said) return;
            // (the last one's clip stops: this one's plays as soon as it's read, unless that's after its words have gone;
            // one that couldn't be read is read again the next time, its words shown meanwhile: 0.94 audit)
            this._reactionSound?.stop();
            clearTimeout(this._reactionClipTimeout);
            void found.clip.then(sound => {
                if (!sound || this.destroyed || this._reactionsSaid !== said || performance.now() - saidAt > REACTION_SHOWN_MS) return;
                this._reactionSound = this.playSound(sound, { falloff: 0.6, maxRange: 160 });
                this._reactionClipTimeout = setTimeout(() => this._reactionSound?.stop(), REACTION_CLIP_MS);
            });

            const { text, background, container } = this.reaction;
            text.text = found.text;
            const width = text.width + 36;
            const height = text.height + 18;
            background.clear().roundRect(-width / 2, -height / 2, width, height, 14).fill({ color: 0x141812, alpha: 0.78 }).stroke({ color: 0xd8c08a, width: 2, alpha: 0.9 });
            container.visible = true;
            container.alpha = 1;
            this._reactionHideTimeout?.kill();
            this._reactionHideTimeout = this.addTimeout(() => {
                container.visible = false;
                this._reactionHideTimeout = undefined;
            }, REACTION_SHOWN_MS);
        });
    }

    showEmote(emote: EmoteDefinition): void {
        this.anims.emote?.kill();
        this.anims.emoteHide?.kill();
        this._emoteHideTimeout?.kill();

        this.playSound("emote", {
            falloff: 0.4,
            maxRange: 128
        });

        const { container, background, image } = this.emote;

        container.visible = true;
        container.scale.set(0);

        const { backgroundTexture, scale } = Loot.getBackgroundAndScale(Loots.fromStringSafe(emote.idString));
        background.setFrame(backgroundTexture ?? "emote_background");
        image.setFrame(emote.idString).setScale(scale ?? 1);

        this.anims.emote = Game.addTween({
            target: container.scale,
            to: { x: 1, y: 1 },
            duration: 250,
            ease: EaseFunctions.backOut,
            onComplete: () => {
                this.anims.emote = undefined;
            }
        });

        this._emoteHideTimeout = this.addTimeout(() => {
            this.anims.emoteHide = Game.addTween({
                target: container.scale,
                to: { x: 0, y: 0 },
                duration: 200,
                ease: EaseFunctions.backIn,
                onComplete: () => {
                    this._emoteHideTimeout = undefined;
                    this.anims.emoteHide = undefined;

                    container.visible = false;
                    this.anims.emote?.kill();
                    this.anims.emote = undefined;
                    this._emoteHideTimeout = undefined;
                }
            });
        }, 2000);
    }

    playAnimation(anim: AnimationType): void {
        switch (anim) {
            case AnimationType.None: {
                this.updateFistsPosition(true);
                this.updateWeapon();
                break;
            }
            case AnimationType.Melee: {
                if (this.activeItem.defType !== DefinitionType.Melee) {
                    console.warn(`Attempted to play melee animation with non-melee item '${this.activeItem.idString}'`);
                    return;
                }
                this.updateFistsPosition(false);
                this.updateWeapon(true);
                const weaponDef = this.activeItem;

                let altFist = Math.random() < 0.5;
                if (!weaponDef.fists.randomFist) altFist = true;
                let previousDuration = 0;

                if (weaponDef.animation) {
                    for (const frame of weaponDef.animation) {
                        const duration = frame.duration;
                        const currentWeapon = this.activeItem;
                        this.addTimeout(() => {
                            if (this.activeItem.idString === currentWeapon.idString) {
                                if (!weaponDef.fists.randomFist || !altFist) {
                                    this.anims.leftFist = Game.addTween({
                                        target: this.images.leftFist,
                                        to: { x: frame.fists.left.x, y: frame.fists.left.y },
                                        duration,
                                        ease: EaseFunctions.cubicOut
                                    });
                                }

                                if (altFist) {
                                    this.anims.rightFist = Game.addTween({
                                        target: this.images.rightFist,
                                        to: { x: frame.fists.right.x, y: frame.fists.right.y },
                                        duration,
                                        ease: EaseFunctions.cubicOut
                                    });
                                }

                                if (weaponDef.image !== undefined && frame.image !== undefined) {
                                    this.anims.weapon = Game.addTween({
                                        target: this.images.weapon,
                                        to: {
                                            x: frame.image.position.x,
                                            y: frame.image.position.y,
                                            angle: frame.image.angle
                                        },
                                        duration,
                                        ease: EaseFunctions.cubicOut
                                    });
                                }
                            }
                        }, previousDuration);
                        previousDuration += duration;
                    }
                }

                // the Gravity Gloves only send this for a plasma burst: the hands push out (the frames above), with
                // no swing sound and no hit (the bolt has its own zap and its own hits; a punch's hit effects showed
                // on whatever stood in front, the cell bars included)
                if (weaponDef.gravity) break;

                if (weaponDef.hitDelay) {
                    clearTimeout(this._meleeSoundTimeoutID);
                    this._meleeSoundTimeoutID = window.setTimeout(() => {
                        this.playSound(
                            weaponDef.swingSound ?? "swing",
                            {
                                falloff: 0.4,
                                maxRange: 96
                            }
                        );
                    }, weaponDef.hitDelay);
                } else {
                    this.playSound(
                        weaponDef.swingSound ?? "swing",
                        {
                            falloff: 0.4,
                            maxRange: 96
                        }
                    );
                }

                if (weaponDef.stopSound && this.meleeStopSound === undefined) {
                    this.meleeStopSound = this.playSound(
                        weaponDef.stopSound,
                        {
                            falloff: 0.4,
                            maxRange: 96
                        }
                    );
                } else {
                    this.meleeStopSound = undefined;
                }

                if (weaponDef.image?.animated) {
                    if (this.meleeAttackCounter >= 1) {
                        this.meleeAttackCounter--;
                    } else {
                        this.meleeAttackCounter++;
                    }
                    this.images.weapon.setFrame(`${weaponDef.idString}${this.meleeAttackCounter <= 0 ? "_used" : ""}`);
                }

                const hitDelay = weaponDef.hitDelay ?? 50;
                this.addTimeout(() => {

                    let hitCount = 0;
                    for (let i = 0; i < (weaponDef.numberOfHits ?? 1); i++) {
                        this.addTimeout((): void => {
                            if (this.activeItem.defType !== DefinitionType.Melee) return;

                            if (hitCount > 0) {
                                this.playSound(
                                    weaponDef.swingSound ?? "swing",
                                    {
                                        falloff: 0.4,
                                        maxRange: 96
                                    }
                                );
                            }

                            hitCount++;

                            const hitbox = getMeleeHitbox(this, this.activeItem);
                            const hits = getMeleeTargets<Player | Obstacle | Projectile | Building>(hitbox, this.activeItem, this, Game.isTeamMode, Game.objects);

                            for (const hit of hits) {
                                const target = hit.object;

                                const angle = Math.atan2(hit.direction.y, hit.direction.x);

                                if (target.isPlayer) {
                                    let sound = this.activeItem.hitSound;
                                    if (sound && weaponDef.numberOfHits !== undefined && weaponDef.numberOfHits > 1) {
                                        sound += `_${hitCount}`;
                                    }
                                    target.hitEffect(hit.position, angle, sound, { kind: "melee", weapon: weaponDef });
                                } else {
                                    // hares have a hit effect too; the optional call keeps any object without one from breaking the frame
                                    (target as Partial<Pick<typeof target, "hitEffect">>).hitEffect?.(hit.position, angle);
                                }
                            }
                        }, (i === 0 ? 0 : (weaponDef.delayBetweenHits ?? 0)));
                    }
                }, hitDelay);
                break;
            }
            case AnimationType.Downed: {
                this.updateFistsPosition(false);

                if (this.images.rightLeg && !this.destroyed) {
                    this.anims.rightLeg = Game.addTween({
                        target: this.images.rightLeg,
                        to: { x: this.images.rightLeg.x - 10, y: this.images.rightLeg.y },
                        duration: 200,
                        ease: EaseFunctions.sineIn,
                        yoyo: true,
                        onComplete: () => {
                            this.anims.rightLeg = undefined;
                        }
                    });

                    this.anims.leftFist = Game.addTween({
                        target: this.images.leftFist,
                        to: { x: this.images.leftFist.x - 10, y: this.images.leftFist.y - 5 },
                        duration: 200,
                        ease: EaseFunctions.sineIn,
                        yoyo: true,
                        onComplete: () => {
                            this.anims.leftFist = undefined;
                        }
                    });
                }

                setTimeout(() => {
                    if (this.images.leftLeg && !this.destroyed) {
                        this.anims.leftLeg = Game.addTween({
                            target: this.images.leftLeg,
                            to: { x: this.images.leftLeg.x - 10, y: this.images.leftLeg.y },
                            duration: 200,
                            ease: EaseFunctions.sineIn,
                            yoyo: true,
                            onComplete: () => {
                                this.anims.leftLeg = undefined;
                            }
                        });

                        this.anims.rightFist = Game.addTween({
                            target: this.images.rightFist,
                            to: { x: this.images.rightFist.x - 10, y: this.images.rightFist.y + 5 },
                            duration: 200,
                            ease: EaseFunctions.sineIn,
                            yoyo: true,
                            onComplete: () => {
                                this.anims.rightFist = undefined;
                            }
                        });
                    }
                }, 200);
                break;
            }
            case AnimationType.GunFire:
            case AnimationType.GunFireAlt: {
                if (this.activeItem.defType !== DefinitionType.Gun) {
                    const name = ({
                        [AnimationType.GunFire]: "GunFire",
                        [AnimationType.GunFireAlt]: "GunFireAlt"
                    })[anim];
                    console.warn(`Attempted to play gun animation (${name}) with non-gun item '${this.activeItem.idString}'`);
                    return;
                }
                const weaponDef = this.activeItem;
                const {
                    image: { position: { x: imgX } }
                } = this._getItemReference() as SingleGunNarrowing;
                const {
                    fists: {
                        left: { x: leftFistX },
                        right: { x: rightFistX }
                    }
                } = weaponDef;

                const isAltFire = weaponDef.isDual
                    ? anim === AnimationType.GunFireAlt
                    : undefined;

                this.updateFistsPosition(false);
                this.updateWeapon(true);
                const recoilAmount = PIXI_SCALE * (1 - weaponDef.recoilMultiplier);

                this.anims.weapon = Game.addTween({
                    target: isAltFire ? this.images.altWeapon : this.images.weapon,
                    to: { x: imgX - recoilAmount },
                    duration: 50,
                    yoyo: true
                });

                if (
                    weaponDef.gasParticles
                    && GameConsole.getBuiltInCVar("cv_cooler_graphics")
                    && GameConsole.getBuiltInCVar("cv_cooler_weapon_gas")
                ) {
                    const offset = weaponDef.isDual
                        ? (isAltFire ? -1 : 1) * weaponDef.leftRightOffset
                        : (weaponDef.bulletOffset ?? 0);

                    const position = Vec.add(
                        this.position,
                        Vec.scale(Vec.rotate(Vec(weaponDef.length, offset), this.rotation), this.sizeMod)
                    );

                    const gas = weaponDef.gasParticles;
                    const halfSpread = 0.5 * gas.spread;

                    ParticleManager.spawnParticles(gas.amount, () => ({
                        frames: "small_gas",
                        layer: this.layer,
                        lifetime: random(gas.minLife, gas.maxSize),
                        scale: {
                            start: 0, end: randomFloat(gas.minSize, gas.maxSize)
                        },
                        position,
                        speed: Vec.fromPolar(
                            this.rotation + Angle.degreesToRadians(
                                randomFloat(-halfSpread, halfSpread)
                            ),
                            randomFloat(gas.minSpeed, gas.maxSpeed)
                        ),
                        zIndex: ZIndexes.BuildingsCeiling - 2,
                        alpha: {
                            start: randomFloat(0.5, 1),
                            end: 0
                        },
                        tint: 0x555555
                    }));
                }

                // the muzzle's flame and the light it casts, the gas it blows out, a shotgun's sparks, a hot barrel's smoke
                // after (managers/combatFx.ts). With them on, they draw the flash: the old picture below is for when
                // they're off (0.86: both showed, one over the other)
                const flameDrawn = CombatFx.muzzle(this, weaponDef, isAltFire);
                // at night the shot's flash lights round the gun's end for a blink (1.05)
                if (!weaponDef.noMuzzleFlash) {
                    NightLight.muzzle(this, weaponDef.length);
                    // (0.98.6: by day a blink of light round the muzzle too, the lighting's)
                    Lighting.muzzle(this, weaponDef.length);
                }

                if (!weaponDef.noMuzzleFlash && !flameDrawn) {
                    const muzzleFlash = this.images.muzzleFlash;

                    muzzleFlash.x = weaponDef.length * PIXI_SCALE;
                    muzzleFlash.y = (isAltFire ? -1 : 1) * this._getOffset();
                    muzzleFlash.setVisible(true);
                    muzzleFlash.alpha = 0.95;
                    muzzleFlash.scale = Vec(
                        randomFloat(0.75, 1.25),
                        randomFloat(0.5, 1.5) * (randomBoolean() ? 1 : -1)
                    );

                    this.anims.muzzleFlashFade?.kill();
                    this.anims.muzzleFlashRecoil?.kill();
                    this.anims.muzzleFlashFade = Game.addTween({
                        target: muzzleFlash,
                        to: { alpha: 0 },
                        duration: 100,
                        onComplete: () => {
                            muzzleFlash.setVisible(false);
                            this.anims.muzzleFlashFade = undefined;
                        }
                    });

                    this.anims.muzzleFlashRecoil = Game.addTween({
                        target: muzzleFlash,
                        to: { x: muzzleFlash.x - recoilAmount },
                        duration: 50,
                        yoyo: true,
                        onComplete: () => {
                            this.anims.muzzleFlashRecoil = undefined;
                        }
                    });
                }


                if (isAltFire !== false) {
                    this.anims.leftFist = Game.addTween({
                        target: this.images.leftFist,
                        to: { x: leftFistX - recoilAmount },
                        duration: 50,
                        yoyo: true,
                        onComplete: () => {
                            this.anims.leftFist = undefined;
                        }
                    });
                }

                if (isAltFire !== true) {
                    this.anims.rightFist = Game.addTween({
                        target: this.images.rightFist,
                        to: { x: rightFistX - recoilAmount },
                        duration: 50,
                        yoyo: true,
                        onComplete: () => {
                            this.anims.rightFist = undefined;
                        }
                    });
                }

                if (weaponDef.cameraShake !== undefined) {
                    CameraManager.shake(weaponDef.cameraShake.duration, weaponDef.cameraShake.intensity);
                }

                if (weaponDef.backblast !== undefined) {
                    const trail = weaponDef.ballistics.trail,
                        backblast = weaponDef.backblast;

                    if (trail && Date.now() - this._lastParticleTrail >= trail.interval) {
                        const offset = weaponDef.isDual
                            ? (isAltFire ? -1 : 1) * weaponDef.leftRightOffset
                            : (weaponDef.bulletOffset ?? 0);

                        const position = Vec.add(
                            this.position,
                            Vec.scale(Vec.rotate(Vec(-backblast.length, offset), this.rotation), this.sizeMod)
                        );

                        ParticleManager.spawnParticles(
                            backblast.particlesAmount,
                            () => ({
                                frames: trail.frame,
                                speed: Vec.fromPolar(
                                    randomRotation(),
                                    randomFloat(backblast.min, backblast.max)
                                ),
                                position,
                                lifetime: backblast.duration,
                                zIndex: ZIndexes.Bullets - 1,
                                scale: randomFloat(backblast.scale.min, backblast.scale.max),
                                alpha: {
                                    start: randomFloat(trail.alpha.min, trail.alpha.max),
                                    end: 0
                                },
                                layer: this.layer,
                                tint: pickRandomInArray([0x8a8a8a, 0x3d3d3d, 0x858585])
                            })
                        );

                        this._lastParticleTrail = Date.now();
                    }
                }

                this.spawnCasingParticles("fire", isAltFire);

                if (this.isCycling) this.spawnCasingParticles("cycle", isAltFire);
                break;
            }
            case AnimationType.GunClick: {
                this.playSound(
                    "gun_click",
                    {
                        falloff: 0.8,
                        maxRange: 48
                    }
                );
                break;
            }
            case AnimationType.ThrowableCook: {
                if (this.activeItem.defType !== DefinitionType.Throwable) {
                    console.warn(`Attempted to play throwable cooking animation with non-throwable item '${this.activeItem.idString}'`);
                    return;
                }

                this.playSound(this.activeItem.c4 ? "c4_pin" : "throwable_pin");

                const def = this.activeItem;
                const projImage = this.images.weapon;
                const pinImage = this.images.altWeapon;

                projImage.visible = true;
                if (def.animation.pinImage) {
                    let pinFrame = def.animation.pinImage;

                    if (def.pinSkin && this.halloweenThrowableSkin && !def.noSkin) {
                        pinFrame += "_halloween";
                    }

                    pinImage.setFrame(pinFrame);
                    pinImage.setPos(35, 0);
                    pinImage.setZIndex(ZIndexes.Players + 1);
                }

                let frame = def.animation.cook.cookingImage ?? def.animation.liveImage;

                if (this.halloweenThrowableSkin && !def.noSkin) {
                    frame += "_halloween";
                }

                this.updateFistsPosition(false);
                projImage.setFrame(frame);

                this.anims.leftFist = Game.addTween({
                    target: this.images.leftFist,
                    to: { x: 35, y: 0 },
                    duration: def.cookTime / 2,
                    onComplete: () => {
                        this.anims.leftFist = Game.addTween({
                            target: this.images.leftFist,
                            to: Vec.scale(def.animation.cook.leftFist, PIXI_SCALE),
                            duration: def.cookTime / 2,
                            onComplete: () => {
                                this.anims.leftFist = undefined;
                            }
                        });

                        if (def.animation.pinImage) {
                            pinImage.visible = true;
                            this.anims.pin = Game.addTween({
                                target: pinImage,
                                duration: def.cookTime / 2,
                                to: {
                                    ...Vec.add(Vec.scale(def.animation.cook.leftFist, PIXI_SCALE), Vec(15, 0))
                                },
                                onComplete: () => {
                                    this.anims.pin = undefined;
                                }
                            });
                        }
                    }
                });

                if (def.cookable && def.animation.leverImage !== undefined) {
                    ParticleManager.spawnParticle({
                        frames: def.animation.leverImage,
                        lifetime: 600,
                        position: Vec.add(
                            this.position,
                            Vec.scale(def.animation.cook.rightFist, this.sizeMod)
                        ),
                        layer: this.layer,
                        zIndex: ZIndexes.Players + 1,
                        speed: Vec.rotate(Vec(8, 8), this.rotation),
                        rotation: this.rotation,
                        alpha: {
                            start: 1,
                            end: 0
                        },
                        scale: {
                            start: 0.8 * this.sizeMod,
                            end: this.sizeMod
                        }
                    });
                }

                this.anims.weapon = Game.addTween({
                    target: projImage,
                    to: { x: 25, y: 10 },
                    duration: def.cookTime / 2,
                    onComplete: () => {
                        this.anims.weapon = undefined;
                    }
                });

                this.anims.rightFist = Game.addTween({
                    target: this.images.rightFist,
                    to: { x: 25, y: 10 },
                    duration: def.cookTime / 2,
                    onComplete: () => {
                        this.anims.weapon = Game.addTween({
                            target: projImage,
                            to: Vec.scale(def.animation.cook.rightFist, PIXI_SCALE),
                            duration: def.cookTime / 2,
                            onComplete: () => {
                                this.anims.weapon = undefined;
                            }
                        });

                        this.anims.rightFist = Game.addTween({
                            target: this.images.rightFist,
                            to: Vec.scale(def.animation.cook.rightFist, PIXI_SCALE),
                            duration: def.cookTime / 2,
                            onComplete: () => {
                                this.anims.rightFist = undefined;
                            }
                        });
                    }
                });

                break;
            }
            case AnimationType.ThrowableThrow: {
                if (this.activeItem.defType !== DefinitionType.Throwable) {
                    console.warn(`Attempted to play throwable throwing animation with non-throwable item '${this.activeItem.idString}'`);
                    return;
                }
                this.playSound("throwable_throw");

                const def = this.activeItem;

                this.images.altWeapon.visible = false;
                const projImage = this.images.weapon;
                projImage.visible = false;

                projImage.setFrame(`${def.idString}${this.halloweenThrowableSkin && !def.noSkin ? "_halloween" : ""}`);

                if (!def.cookable && def.animation.leverImage !== undefined) {
                    ParticleManager.spawnParticle({
                        frames: def.animation.leverImage,
                        lifetime: 600,
                        layer: this.layer,
                        position: Vec.add(
                            this.position,
                            Vec.scale(def.animation.cook.rightFist, this.sizeMod)
                        ),
                        zIndex: ZIndexes.Players + 1,
                        speed: Vec.rotate(Vec(8, 8), this.rotation),
                        rotation: this.rotation,
                        alpha: {
                            start: 1,
                            end: 0
                        },
                        scale: {
                            start: 0.8 * this.sizeMod,
                            end: this.sizeMod
                        }
                    });
                }

                this.anims.rightFist?.kill();
                this.anims.leftFist?.kill();
                this.anims.weapon?.kill();

                this.anims.leftFist = Game.addTween({
                    target: this.images.leftFist,
                    to: Vec.scale(def.animation.throw.leftFist, PIXI_SCALE),
                    duration: def.throwTime,
                    onComplete: () => {
                        this.anims.leftFist = undefined;
                        projImage.setVisible(true);
                        projImage.setFrame(`${def.idString}${this.halloweenThrowableSkin && !def.noSkin ? "_halloween" : ""}`);
                        this.updateFistsPosition(true);
                    }
                });

                this.anims.rightFist = Game.addTween({
                    target: this.images.rightFist,
                    to: Vec.scale(def.animation.throw.rightFist, PIXI_SCALE),
                    duration: def.throwTime,
                    onComplete: () => {
                        this.anims.rightFist = undefined;
                    }
                });

                break;
            }
            case AnimationType.Revive: {
                this.images.weapon.setVisible(false);
                this.images.altWeapon.setVisible(false);
                this.images.muzzleFlash.setVisible(false);
                this.images.leftFist.setZIndex(4);
                this.images.rightFist.setZIndex(4);
                this.anims.leftFist = Game.addTween({
                    target: this.images.leftFist,
                    to: Vec(28, -45),
                    duration: 100,
                    onComplete: () => {
                        this.anims.leftFist = undefined;
                    }
                });
                this.anims.rightFist = Game.addTween({
                    target: this.images.rightFist,
                    to: Vec(58, 48),
                    duration: 100,
                    onComplete: () => {
                        this.anims.rightFist = undefined;
                    }
                });
                break;
            }
        }
    }

    /**
     * `angle`: the surface's outward normal where it struck (a bullet), or the blow's direction (melee). `gore`: what
     * struck, for the blood it throws (Settings → Graphics → Blood & gore; see gore.ts)
     */
    hitEffect(position: Vector, angle: number, sound?: string, gore?: GoreHit): void {
        // a bullet into a body: its thud, or the ring of a helmet or the "tunk" of a vest it struck, sparks off the steel
        const bulletOnBody = gore?.kind === "bullet" && sound === undefined && !this.activeDisguise && !this.hasBubble;
        const armor = bulletOnBody && !this.bloodless ? this.armorStruck() : undefined;
        if (armor) CombatFx.armorHit(position, angle, this.layer, armor);
        if (!Game.gameOver && !(bulletOnBody && CombatSounds.bodyHit(this.id, position, this.layer, armor, this.bloodless))) {
            SoundManager.play(
                sound ?? `${
                    this.activeDisguise
                        ? MaterialSounds[this.activeDisguise.material]?.hit ?? this.activeDisguise.material
                        : this.hasBubble ? PerkData[PerkIds.ExperimentalForcefield].shieldHitSound : "player"
                }_hit_${randomBoolean() ? "1" : "2"}`,
                {
                    position,
                    falloff: 0.2,
                    maxRange: 96,
                    layer: this.layer
                }
            );
        }

        if (this.activeDisguise || this.hasBubble) {
            // a disguise or a shield takes the hit: its own particle, no blood
            let particle = this.activeDisguise ? (this.activeDisguise.frames?.particle ?? `${this.activeDisguise.idString}_particle`) : PerkData[PerkIds.ExperimentalForcefield].shieldParticle;

            if (this.activeDisguise?.particleVariations) particle += `_${random(1, this.activeDisguise.particleVariations)}`;

            ParticleManager.spawnParticle({
                frames: particle,
                zIndex: PLAYER_PARTICLE_ZINDEX,
                layer: this.layer,
                position,
                lifetime: 1000,
                scale: {
                    start: 0.5 * this.sizeMod,
                    end: this.sizeMod
                },
                alpha: {
                    start: 1,
                    end: 0
                },
                speed: Vec.fromPolar(angle, randomFloat(0.5, 1))
            });
            return;
        }

        if (sound === "bleed" && gore === undefined) GoreManager.bleed(this);
        else GoreManager.hit(this, position, angle, gore);
    }

    destroy(): void {
        GravityFx.setBeam(this, undefined);
        GravityFx.dressHands(this, false);
        // (its chute too, 1.06: one going out of view in the air; its octo arms, 1.10)
        JumpInView.forget(this);
        OctoFx.forget(this);
        super.destroy();

        const { images, emote, teammateName, anims } = this;

        images.aimTrail?.destroy();
        images.altAimTrail?.destroy();
        images.vest.destroy();
        images.body.destroy();
        images.leftFist.destroy();
        images.rightFist.destroy();
        images.leftLeg?.destroy();
        images.rightLeg?.destroy();
        images.backpack.destroy();
        images.helmet.destroy();
        images.weapon.destroy();
        images.altWeapon.destroy();
        images.muzzleFlash.destroy();
        images.waterOverlay.destroy();
        images.blood.destroy();
        this.disguiseContainer?.destroy();
        this.healthBarFadeTimeout?.kill();
        this.healthBarTween?.kill();
        images.healthBar?.destroy();

        emote.image.destroy();
        emote.background.destroy();
        emote.container.destroy();

        this._reactionHideTimeout?.kill();
        clearTimeout(this._reactionClipTimeout);
        this._reactionSound?.stop();
        this.reaction.text.destroy();
        this.reaction.background.destroy();
        this.reaction.container.destroy();

        teammateName?.text.destroy();
        teammateName?.badge?.destroy();
        teammateName?.container.destroy();

        anims.emoteHide?.kill();
        anims.waterOverlay?.kill();
        anims.emote?.kill();
        anims.leftFist?.kill();
        anims.rightFist?.kill();
        anims.weapon?.kill();
        anims.muzzleFlashFade?.kill();
        anims.muzzleFlashRecoil?.kill();
        anims.sizeMod?.kill();
        anims.climb?.kill();
        anims.climbFade?.kill();
        anims.land?.kill();

        images.backMeleeSprite?.destroy();

        this.healingParticlesEmitter.destroy();
        this.smokeParticlesEmitter?.destroy();
        this.actionSound?.stop();
        clearInterval(this.bleedEffectInterval);
        if (this.isActivePlayer) $("#action-container").hide();
    }
}
